import type { ClientApi, ContratApi, SubscriptionRow, TypeContratClient } from "./types";

const TYPE_LABELS: Record<TypeContratClient, string> = {
  MENAGE: "Ménage",
  ENTREPRISE: "Entreprise",
  PROFESSIONNEL: "Professionnel",
  CHANTIER: "Chantier",
};

export function parseDecimal(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function clientDisplayName(c: ClientApi): string {
  const raison = c.raisonSociale?.trim();
  const entreprise = c.nomEntreprise?.trim();
  if (raison) return raison;
  if (entreprise) return entreprise;
  const parts = [c.prenom, c.nom].filter(Boolean).join(" ").trim();
  return parts || `Client #${c.id}`;
}

export function clientTypeLabel(type: string): string {
  if (type in TYPE_LABELS) return TYPE_LABELS[type as TypeContratClient];
  return type || "—";
}

export function formatMoney(amount: number, devise: string): string {
  const d = devise === "CDF" ? "CDF" : "USD";
  return `${amount.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ${d}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR");
}

export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}

export function isCalendarExpired(dateFin: string | null | undefined): boolean {
  if (!dateFin) return false;
  const end = new Date(dateFin);
  if (Number.isNaN(end.getTime())) return false;
  return end < startOfToday();
}

export function expiresWithinDays(dateFin: string | null | undefined, days: number): boolean {
  if (!dateFin) return false;
  const end = new Date(dateFin);
  if (Number.isNaN(end.getTime())) return false;
  const now = startOfToday();
  const limit = addDays(now, days);
  return end >= now && end <= limit;
}

export function dotationsFromClient(c: ClientApi): string {
  if (c.nombreBacs != null && c.nombreBacs > 0) {
    return `${c.nombreBacs} bac(s)`;
  }
  const vol = c.volumeDechets?.trim();
  if (vol) return vol;
  return "À structurer";
}

export function buildSubscriptionRow(contrat: ContratApi, client: ClientApi): SubscriptionRow {
  const montant = parseDecimal(contrat.montant);
  const solde = parseDecimal(client.soldeClient);
  const planLabel =
    contrat.planAbonnement?.nom?.trim() ||
    contrat.categorie?.nom?.trim() ||
    "Non défini";

  const calendarExpired = isCalendarExpired(contrat.dateFin);
  const expireBientot =
    contrat.statut === "ACTIF" && expiresWithinDays(contrat.dateFin, 30);

  let statutTone: SubscriptionRow["statutTone"] = "slate";
  if (contrat.statut === "EXPIRE" || contrat.statut === "RESILIE" || calendarExpired) {
    statutTone = "red";
  } else if (contrat.statut === "SUSPENDU" || expireBientot) {
    statutTone = "orange";
  } else if (contrat.statut === "ACTIF") {
    statutTone = "green";
  }

  const sansPlanning =
    contrat.statut === "ACTIF" &&
    contrat.prochainPassage == null &&
    (contrat.nbPassagesPrevus == null || contrat.nbPassagesPrevus === 0);

  const prochainPassageLabel =
    contrat.prochainPassage != null ? formatDate(contrat.prochainPassage) : "À connecter";

  const passagesPrevusLabel =
    contrat.nbPassagesPrevus != null && contrat.nbPassagesPrevus > 0
      ? String(contrat.nbPassagesPrevus)
      : "—";

  return {
    contratId: contrat.id,
    clientId: client.id,
    clientDisplay: clientDisplayName(client),
    clientType: String(client.type ?? ""),
    clientTypeLabel: clientTypeLabel(String(client.type ?? "")),
    zoneLabel: client.zone?.nom?.trim() || "—",
    planLabel,
    frequence: contrat.frequence,
    prixLabel: formatMoney(montant, contrat.devise),
    passagesPrevusLabel,
    dotationsLabel: dotationsFromClient(client),
    statutContrat: contrat.statut,
    statutTone,
    detteLabel:
      solde > 0
        ? formatMoney(solde, "USD")
        : solde < 0
          ? `${formatMoney(Math.abs(solde), "USD")} (crédit)`
          : "0 USD",
    hasDebt: solde > 0,
    prochainPassageLabel,
    sansPlanning,
    expireBientot,
    calendarExpired,
  };
}
